/* Read the merchant's existing brand charts; never fabricate measurements. */
(() => {
  'use strict';
  const initialized = new WeakSet();
  const clean = value => String(value || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  function sizeKey(value) {
    const text = clean(value).toUpperCase().replace(/\bSIZE\s*:\s*/g, '').replace(/[\s._-]/g, '');
    const aliases = {SMALL:'S',MEDIUM:'M',LARGE:'L',XSMALL:'XS',EXTRASMALL:'XS',XXSMALL:'XXS',XLARGE:'XL',EXTRALARGE:'XL',XXL:'2XL',XXXL:'3XL',XXXXL:'4XL',OS:'ONESIZE','O/S':'ONESIZE',ONESIZEFITSMOST:'ONESIZE',ONESIZEFITSALL:'ONESIZE',QUEENSIZE:'CURVYSIZE'};
    if (aliases[text]) return aliases[text];
    return text.split('/').map(part => aliases[part] || part).join('/');
  }
  function dimension(value) {
    const label = clean(value).toLowerCase();
    if (/^bust\b/.test(label)) return 'Bust';
    if (/^waist\b/.test(label)) return 'Waist';
    if (/^hips?\b/.test(label)) return 'Hips';
    if (/^(?:bra )?cup(?: size)?$/.test(label)) return 'Cup';
    if (/^dress(?: size)?$/.test(label)) return 'Dress';
    return null;
  }
  function readChart(table, size) {
    const rows = [...table.querySelectorAll('tr')].filter(row => row.closest('table') === table).map(row => [...row.children].filter(cell => /^(TD|TH)$/.test(cell.tagName)));
    if (rows.length < 2 || rows.some(row => row.some(cell => Number(cell.getAttribute('colspan') || 1) > 1 || Number(cell.getAttribute('rowspan') || 1) > 1))) return null;
    const textRows = rows.map(row => row.map(cell => clean(cell.textContent)));
    const header = textRows[0], key = sizeKey(size);
    if (!key) return null;
    const values = {};
    if (header.filter(label => dimension(label)).length >= 3) {
      const row = textRows.slice(1).find(cells => sizeKey(cells[0]) === key);
      if (!row) return null;
      header.forEach((label, index) => { const field = dimension(label); if (field && index > 0) values[field] = row[index]; });
    } else {
      const index = header.findIndex((label, position) => position > 0 && sizeKey(label) === key);
      if (index < 0) return null;
      textRows.slice(1).forEach(row => { const field = dimension(row[0]); if (field) values[field] = row[index]; });
    }
    if (!['Bust','Waist','Hips'].every(field => values[field] && /\d/.test(values[field]))) return null;
    const chartText = table.closest('[data-es-size-chart]')?.textContent || table.textContent;
    const inches = /\binches\b/i.test(chartText);
    return ['Bust','Waist','Hips','Cup','Dress'].filter(label => values[label] && !/^(?:-|n\/?a)$/i.test(values[label])).map(label => {
      let value = clean(values[label]).replace(/(\d)\s*(in\b|cm\b)/gi, '$1 $2');
      if (['Bust','Waist','Hips'].includes(label) && inches && !/\b(?:in|inches|cm)\b|["\u2033]/i.test(value)) value += ' in';
      return {label,value};
    });
  }
  function measurements(section, size) {
    const matches = [...section.querySelectorAll('[data-es-size-chart] table')].map(table => readChart(table, size)).filter(Boolean);
    const unique = [...new Map(matches.map(match => [JSON.stringify(match), match])).values()];
    return unique.length === 1 ? unique[0] : null;
  }
  function update(box, size = box.dataset.size) {
    const section = box.closest('[data-section-type="product"]');
    if (!section || !size) { box.hidden = true; return; }
    const facts = measurements(section, size);
    if (!facts) { box.hidden = true; return; }
    box.querySelector('[data-es-fit-heading]').textContent = box.dataset.heading.replace('__SIZE__', clean(size));
    const node = fact => {
      const item = document.createElement('div'); item.className = 'es-fit-measurement';
      const label = document.createElement('span'); label.className = 'es-fit-measurement__label'; label.textContent = fact.label;
      const value = document.createElement('span'); value.className = 'es-fit-measurement__value'; value.textContent = fact.value;
      item.append(label, value); return item;
    };
    box.querySelector('[data-es-fit-values]').replaceChildren(...facts.filter(fact => !['Cup','Dress'].includes(fact.label)).map(node));
    const extra = facts.filter(fact => ['Cup','Dress'].includes(fact.label));
    const secondary = box.querySelector('[data-es-fit-secondary]');
    secondary.replaceChildren(...extra.map(node)); secondary.hidden = extra.length === 0;
    box.hidden = false;
  }
  function initialize(root) {
    root.querySelectorAll('[data-section-type="product"] .product-form__info-list').forEach(list => {
      list.hidden = list.children.length === 0 && !list.textContent.trim();
    });
    const charts = [...root.querySelectorAll('[data-es-size-chart]')];
    charts.forEach(chart => { chart.classList.add('es-size-chart-enhanced'); chart.querySelectorAll('table').forEach(table => {
      if (table.parentElement.classList.contains('es-size-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'es-size-scroll'; wrapper.tabIndex = 0; wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', chart.dataset.scrollLabel || 'Size guide');
      table.before(wrapper); wrapper.append(table);
    }); });
    root.querySelectorAll('[data-es-selected-fit]').forEach(box => {
      if (initialized.has(box)) return;
      initialized.add(box); update(box);
    });
  }
  initialize(document);
  document.addEventListener('variant:changed', event => {
    const {formId, variant} = event.detail || {};
    const form = formId && document.getElementById(formId);
    const wishlist = form?.closest('[data-section-type="product"]')?.querySelector('.es-gallery-wishlist');
    if (wishlist && variant) {
      wishlist.dataset.variantId = variant.id;
      document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
    }
    document.querySelectorAll('[data-es-selected-fit]').forEach(box => {
      if (box.dataset.form === formId) update(box, variant ? variant[`option${box.dataset.position}`] : null);
    });
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href^="#es-"]');
    if (!link || !link.closest('[data-section-type="product"]') || !/^#es-(?:product-fit|how-measure|delivery)-/.test(link.hash)) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    event.preventDefault(); event.stopPropagation();
    if (link.hash.startsWith('#es-how-measure-')) {
      const instructions = target.querySelector('details'); if (instructions) instructions.open = true;
    }
    requestAnimationFrame(() => {
      const header = document.querySelector('[data-es-header] .header');
      const declared = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const offset = Math.max(declared, header?.getBoundingClientRect().height || 0) + 12;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({top:Math.max(0, top),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    });
  }, true);
  document.addEventListener('product:rerender' , () => initialize(document));
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  // Native variant rendering replaces the selector HTML; initialize only new boxes/charts.
  const main = document.getElementById('main');
  if (main) new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-es-selected-fit],[data-es-size-chart]') || node.querySelector('[data-es-selected-fit],[data-es-size-chart]'))))) initialize(main);
  }).observe(main, {childList:true,subtree:true});
})();
